import { Flex, ScrollAreaAutosize } from "@mantine/core";
import { BoxTitle, Container } from "./StyledComponents";
import styled, { keyframes } from "styled-components";
import { theme, media } from "../../theme";
import { UsersIcon, ClockIcon, BuildingOfficeIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { getLiveTodayShifts } from "../../api/dashboardApi";
import { useFilterContext } from "../../context/DashboardFilterContext";
import dayjs from "dayjs";



const TodayShifts = ({ flex }: { flex: any }) => {
  const { filters } = useFilterContext();
  const [data, setData] = useState<any>({});

  useEffect(() => {
    const getShifts = async () => {
      try {
        const response = await getLiveTodayShifts(filters);
        setData(response?.data);
      } catch (error) {}
    };
    getShifts();
  }, [filters]);

  return (
    <Container $flex={flex} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon><ClockIcon width={20} /></HeaderIcon>
          <BoxTitle style={{ fontSize: '17px', fontWeight: 600 }}>Bugünün Vardiyaları</BoxTitle>
        </Flex>
        
        <ChipsGroup>
          <StatChip $accent="#6366F1">
            <ChipVal $accent="#6366F1">{data?.total ?? "0"}</ChipVal>
            <ChipLbl>Toplam</ChipLbl>
          </StatChip>

          <StatChip $accent="#10B981">
            <ActiveIndicator />
            <ChipVal $accent="#10B981">{data?.active ?? "0"}</ChipVal>
            <ChipLbl>Aktif</ChipLbl>
          </StatChip>
        </ChipsGroup>
      </HeaderRow>

      <ScrollAreaAutosize h="100%" scrollbarSize={4} style={{ flex: 1, paddingTop: '8px' }}>
        {/*
          ⚠ Boş kutu, "bugün vardiya yok" ile "veri gelmedi"yi ayırt
          ettirmiyordu (plan 2.60). İşlev ve başlık aynı kaldı; yalnızca
          boşluğun sebebi yazılıyor.
        */}
        {data?.shifts && data.shifts.length === 0 && (
          <BosDurum>Bugün için atanmış vardiya yok</BosDurum>
        )}
        <ShiftList>
          {data?.shifts?.map((shift: any, i: number) => {
            const st = STATUS[shift?.status] ?? STATUS[0];
            return (
              <ShiftCard key={i} $accent={st.color} $delay={i * 45}>
                <ShiftLeft>
                  <DeptRow>
                    <BuildingOfficeIcon width={14} color="#94A3B8" />
                    <ShiftDept title={shift?.department}>{shift?.department}</ShiftDept>
                  </DeptRow>
                  <ShiftTime>
                    {dayjs(shift?.startTime).format("HH:mm")} – {dayjs(shift?.endTime).format("HH:mm")}
                  </ShiftTime>
                </ShiftLeft>

                <ShiftRight>
                  <EmpPill>
                    <UsersIcon width={13} />
                    <span>{shift?.department === "Genel" ? "Herkes" : `${shift?.count} Kişi`}</span>
                  </EmpPill>
                  <StatusPill $color={st.color} $bg={st.bg} $border={st.border}>
                    {st.dot && <PulsingDot />}
                    <span>{st.label}</span>
                  </StatusPill>
                </ShiftRight>
              </ShiftCard>
            );
          })}
        </ShiftList>
      </ScrollAreaAutosize>
    </Container>
  );
};

export default TodayShifts;


const BosDurum = styled.div`
  padding: 24px 8px;
  text-align: center;
  font-size: 13.5px;
  /* Sıkı satır yüksekliği Türkçe büyük harflerin tepesini kırpıyor. */
  line-height: 19px;
  color: #94a3b8;
`;

const fadeUp = keyframes` 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
`;

const pulseDot = keyframes` 
  0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); } 
  60% { box-shadow: 0 0 0 7px rgba(16,185,129,0); } 
`;


const STATUS = [
  { color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", label: "Aktif", dot: true },
  { color: "#D97706", bg: "#FFFBEB", border: "#FDE68A", label: "Başlayacak", dot: false },
  { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Sona Erdi", dot: false },
];

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;

  ${media.mobile} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const HeaderIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChipsGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatChip = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  height: 42px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-3px) scale(1.05);
    background: #ffffff;
    border-color: ${(p) => p.$accent}40; 
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ChipVal = styled.span<{ $accent: string }>`
  font-size: 16px;
  font-weight: 800;
  color: ${(p) => p.$accent};
  line-height: 1;
`;

const ChipLbl = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ActiveIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10B981;
  animation: ${pulseDot} 2s infinite;
  flex-shrink: 0;
`;

const ShiftList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 4px; /* Yanlardan milimetrik daralma */
`;

const ShiftCard = styled.div<{ $accent: string; $delay: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin: 0 2px;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  background: #ffffff;
  border-left: 4px solid ${(p) => p.$accent};
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${p => p.$delay}ms;
  gap: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px) scale(1.015);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
    border-color: ${(p) => p.$accent}30;
    z-index: 5;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ShiftLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const DeptRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ShiftDept = styled.div`
  font-size: 15px;
  font-weight: 500; 
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShiftTime = styled.div`
  font-size: 13px;
  color: #64748b;
  font-weight: 400;
`;

const ShiftRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const EmpPill = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 50px;
  font-size: 12px;
  color: #475569;
`;

const StatusPill = styled.div<{ $color: string; $bg: string; $border: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 500; 
  color: ${(p) => p.$color};
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  min-width: 85px;
  justify-content: center;
`;

const PulsingDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #10B981;
  animation: ${pulseDot} 2s infinite;
`;